import type { ApolloCache } from '@frameio/graphql-client';
import { updateQuery } from '@frameio/graphql-client';
import { TRANSFER_JOB_MAX_HISTORY } from '@frameio/graphql-client/client-schema/transfers/state/transferJobs';
import type { Scalars } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { TRANSFER_SHEET_THUMBNAILS_COUNT } from '@frameio/shared/transfers/constants';
import { useDeleteTransferBatchesMutation } from '../gql/mutations/generated/DeleteTransferBatches';
import type { GetTransferBatchesForAccountQuery } from '../gql/queries/generated/GetTransferBatchesForAccount';
import { GetTransferBatchesForAccount } from '../gql/queries/generated/GetTransferBatchesForAccount';
import { useUploaderWorker } from './useUploaderWorker';

export type TransferBatchIds = {
  id: Scalars['ID']['output'];
  localJobId?: Scalars['ID']['output'];
};

export function useDeleteTransferBatches() {
  const uploader = useUploaderWorker();
  const [deleteTransferBatches] = useDeleteTransferBatchesMutation();
  const accountId = useActiveAccountId();

  const deleteTransferBatchesCallback = async (
    transferBatchIds: TransferBatchIds[]
  ) => {
    const batchIds: string[] = [];
    const batchIdToJobIdMap = new Map<
      Scalars['ID']['output'],
      Scalars['ID']['output']
    >();

    transferBatchIds.forEach(({ id, localJobId }) => {
      batchIds.push(id);

      batchIdToJobIdMap.set(id, localJobId ?? id);
    });

    await deleteTransferBatches({
      optimisticResponse: {
        __typename: 'Mutation',
        deleteTransferBatches: {
          __typename: 'DeleteTransferBatchesPayload',
          result: batchIds.map((id) => ({
            id,
          })),
          successful: true,
        },
      },
      update(cache: ApolloCache<any>, result) {
        const deletedTransferBatches =
          result?.data?.deleteTransferBatches.result;

        (deletedTransferBatches ?? []).forEach((transferBatch) => {
          const jobId = batchIdToJobIdMap.get(transferBatch?.id);

          if (jobId) {
            uploader.worker.removeJob(jobId);
          }
        });

        updateQuery(
          cache,
          GetTransferBatchesForAccount,
          {
            accountId,
            pagination: { pageSize: TRANSFER_JOB_MAX_HISTORY },
            assetPagination: { first: TRANSFER_SHEET_THUMBNAILS_COUNT },
          },
          (draft: GetTransferBatchesForAccountQuery) => {
            if (!draft || !draft.transferBatchesForAccount) return;

            draft.transferBatchesForAccount.result =
              draft.transferBatchesForAccount.result.filter(
                (transferBatch) => !batchIds.includes(transferBatch.id)
              );
          }
        );
      },
      variables: {
        transferBatchIds: batchIds,
      },
    });
  };

  return {
    deleteTransferBatches: deleteTransferBatchesCallback,
  };
}
